import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const VOUCHER_CONTEXT_READY_EVENT = 'gc:voucher-context-ready';
const VOUCHER_CONTEXT_COOKIE_KEY = 'gc_voucher_context';

function parseVoucherConfigValue(rawConfig) {
  if (!rawConfig) return null;
  if (typeof rawConfig === 'object') return rawConfig;

  let candidate = String(rawConfig || '').trim();
  if (!candidate) return null;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const parsedConfig = JSON.parse(candidate);

      if (parsedConfig && typeof parsedConfig === 'object') {
        return parsedConfig;
      }

      if (typeof parsedConfig === 'string') {
        candidate = parsedConfig.trim();
        continue;
      }
    } catch (_error) {}

    candidate = candidate
      .replace(/\\"/g, '"')
      .replace(/=>/g, ':')
      .replace(/\bnil\b/g, 'null');
  }

  return null;
}

function readVoucherContextFromCookie() {
  try {
    const cookies = String(document.cookie || '').split('; ');
    const match = cookies.find((cookie) => cookie.startsWith(`${VOUCHER_CONTEXT_COOKIE_KEY}=`));
    if (!match) return null;

    return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
  } catch (_error) {
    return null;
  }
}

function getResolvedVoucherConfig() {
  const requestedHandle = String(new URLSearchParams(window.location.search).get('gc_campaign') || '')
    .trim()
    .toLowerCase();
  const activeContext = window.Theme?.shopifyStorefront?.activeVoucherContext || null;
  const cookieContext = readVoucherContextFromCookie();

  if (
    requestedHandle &&
    activeContext &&
    String(activeContext.handle || '').trim().toLowerCase() === requestedHandle &&
    activeContext.config
  ) {
    return parseVoucherConfigValue(activeContext.config);
  }

  if (
    requestedHandle &&
    cookieContext &&
    String(cookieContext.handle || '').trim().toLowerCase() === requestedHandle &&
    cookieContext.config
  ) {
    return parseVoucherConfigValue(cookieContext.config);
  }

  if (!requestedHandle && activeContext?.config) {
    return parseVoucherConfigValue(activeContext.config);
  }

  if (!requestedHandle && cookieContext?.config) {
    return parseVoucherConfigValue(cookieContext.config);
  }

  return parseVoucherConfigValue(window.Theme?.shopifyStorefront?.defaultVoucherConfig || null);
}

function getVoucherSet(config) {
  const setValue = config?.set;
  if (Array.isArray(setValue)) {
    return setValue[0] && typeof setValue[0] === 'object' ? setValue[0] : {};
  }

  return setValue && typeof setValue === 'object' ? setValue : {};
}

function getDiscountPercent(discountValue) {
  const value = String(discountValue || '').trim();
  if (!value) return 10;
  if (value.endsWith('%')) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 10;
  }

  const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 10;
}

function formatMoney(cents, currencyCode) {
  const amount = Number(cents || 0) / 100;
  const locale = document.documentElement.lang || 'en-US';
  const code = currencyCode || window.Shopify?.currency?.active || window.Shopify?.currency?.iso_code || 'USD';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_error) {
    return `$${amount.toFixed(2)}`;
  }
}

function shouldApplyVoucherCardPricing(container) {
  if (!(container instanceof HTMLElement)) return false;

  return Boolean(
    container.closest(
      'product-card, .resource-card, .predictive-search-results__card, .predictive-search-results__card--product, [data-product-card-cart-toggle]'
    )
  );
}

function getSelectedSellingPlanPriceContext(container) {
  if (!(container instanceof HTMLElement)) return null;

  const form = container.closest('product-form-component, form, [data-product-card-cart-toggle]');
  if (!(form instanceof HTMLElement)) return null;

  const sellingPlanSelect = form.querySelector(
    '[data-product-card-selling-plan], [data-product-selling-plan]'
  );
  if (!(sellingPlanSelect instanceof HTMLSelectElement)) return null;

  const selectedOption = sellingPlanSelect.options[sellingPlanSelect.selectedIndex];
  if (!(selectedOption instanceof HTMLOptionElement)) return null;

  const priceCents = Number(selectedOption.dataset.priceCents || 0);
  const compareAtPriceCents = Number(
    selectedOption.dataset.compareAtPriceCents || priceCents || 0
  );

  if (!(priceCents > 0)) return null;

  return {
    priceCents,
    compareAtPriceCents:
      compareAtPriceCents > 0 ? compareAtPriceCents : priceCents,
  };
}

function applyVoucherPriceToContainer(container) {
  if (!(container instanceof HTMLElement) || !shouldApplyVoucherCardPricing(container)) return;

  const voucherSet = getVoucherSet(getResolvedVoucherConfig());
  const discountPercent = getDiscountPercent(voucherSet.discount_value || '10%');
  if (!(discountPercent > 0)) return;

  const currencyCode = container.dataset.currencyCode || 'USD';
  const sellingPlanPriceContext = getSelectedSellingPlanPriceContext(container);
  const priceKind = sellingPlanPriceContext ? 'single' : container.dataset.priceKind || 'single';
  const priceCents = Number(
    sellingPlanPriceContext?.priceCents || container.dataset.priceCents || 0
  );
  const compareAtPriceCents = Number(
    sellingPlanPriceContext?.compareAtPriceCents || priceCents || 0
  );
  const voucherBaseSinglePriceCents =
    compareAtPriceCents > priceCents ? compareAtPriceCents : priceCents;
  const priceMinCents = Number(container.dataset.priceMinCents || priceCents);
  const priceMaxCents = Number(container.dataset.priceMaxCents || priceCents);
  const unitPrice = container.querySelector('.unit-price');
  const preservedUnitPrice = unitPrice ? unitPrice.cloneNode(true) : null;

  const originalDisplay =
    priceKind === 'range' && priceMinCents !== priceMaxCents
      ? `${formatMoney(priceMinCents, currencyCode)} - ${formatMoney(priceMaxCents, currencyCode)}`
      : formatMoney(compareAtPriceCents, currencyCode);

  const discountedMin = Math.max(Math.round(priceMinCents * (1 - discountPercent / 100)), 0);
  const discountedMax = Math.max(Math.round(priceMaxCents * (1 - discountPercent / 100)), 0);
  const discountedSingle = Math.max(
    Math.round(voucherBaseSinglePriceCents * (1 - discountPercent / 100)),
    0
  );
  const discountedDisplay =
    priceKind === 'range' && priceMinCents !== priceMaxCents
      ? `${formatMoney(discountedMin, currencyCode)} - ${formatMoney(discountedMax, currencyCode)}`
      : formatMoney(discountedSingle, currencyCode);

  container.innerHTML = `
    <span role="group">
      <span class="visually-hidden">Regular price&nbsp;</span>
      <span class="line-through font-sans font-normal!">${originalDisplay}</span>
    </span>
    <span role="group">
      <span class="visually-hidden">Sale price&nbsp;</span>
      <span class="text-pepper-500 font-serif font-normal!">${discountedDisplay}</span>
    </span>
  `;

  if (preservedUnitPrice instanceof HTMLElement) {
    container.appendChild(preservedUnitPrice);
  }
}

function applyVoucherPricingToAll(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-voucher-price-container]').forEach((container) => {
    applyVoucherPriceToContainer(/** @type {HTMLElement} */ (container));
  });
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  sellingPlanChangeRoot = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    window.addEventListener(VOUCHER_CONTEXT_READY_EVENT, this.applyVoucherPricing);
    this.sellingPlanChangeRoot = this.closest(
      'product-card, [data-product-card-cart-toggle], product-form-component, .resource-card, .predictive-search-results__card, .predictive-search-results__card--product'
    );
    this.sellingPlanChangeRoot?.addEventListener('change', this.handleSellingPlanChange);
    this.applyVoucherPricing();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    window.removeEventListener(VOUCHER_CONTEXT_READY_EVENT, this.applyVoucherPricing);
    this.sellingPlanChangeRoot?.removeEventListener('change', this.handleSellingPlanChange);
    this.sellingPlanChangeRoot = null;
  }

  applyVoucherPricing = () => {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (priceContainer instanceof HTMLElement) {
      applyVoucherPriceToContainer(priceContainer);
    }
  };

  handleSellingPlanChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-product-card-selling-plan], [data-product-selling-plan]')) return;
    this.applyVoucherPricing();
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);
    this.applyVoucherPricing();

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => applyVoucherPricingToAll(), { once: true });
} else {
  applyVoucherPricingToAll();
}

window.addEventListener(VOUCHER_CONTEXT_READY_EVENT, () => applyVoucherPricingToAll());
